import { useMemo, useState } from 'react';
import { BUCKETS, type Bucket, type Command, type Perspective } from '@integrate/contracts';
import {
  ORG_HEAD_GROUP,
  bucketMetaFor,
  countsInHeadcount,
  currentBucket,
  editableFields,
  fmt,
  fmtMoney,
  fmtMoneyCompact,
  getActiveLevels,
  getEmpField,
  getHeadFor,
  getHeadSource,
  groupNodeId,
  hiringCostOf,
  loadedCost,
  resolveField,
  severanceCostOf,
  severanceMonths,
  severanceTier,
  strandedEligible,
  strandedPlanOf,
  targetPlacementValues,
  targetUnitOptions,
  tsaReplacementSide,
  type BuildUp,
  type GroupPathEntry,
  type Person,
  type SeparationState,
} from '@integrate/core';
import type { PhaseId } from '../phases.js';
import { LiveField } from './live-input.js';
import { Section } from './sections.js';
import { removeHeadCommand } from './heads.js';

/** The three mitigation choices, with the sentence each one is making. Ported from `:5172`. */
const STRANDED_ACTIONS = [
  {
    key: 'keep' as const,
    label: 'Keep',
    title: 'No mitigation planned — stays in the residual steady-state stranded cost',
  },
  {
    key: 'redeploy' as const,
    label: 'Redeploy',
    title:
      'Capacity absorbed into the remaining business from month M — no cash effect, stops counting as stranded',
  },
  {
    key: 'rightsize' as const,
    label: 'Rightsize',
    title: 'Leaves RemainCo at month M — one-off severance, full loaded cost saved from M',
  },
];

const PERSPECTIVE_LABEL: Record<Perspective, string> = {
  'as-is': 'As-is',
  remain: 'RemainCo',
  'carve-out': 'Carve-out',
};

/**
 * A person, ported from `openPersonPanel` (orgchart.html:5207).
 *
 * Three things this panel does that the walking skeleton's did not, and each of them was a reason the
 * client could not be used for real work:
 *
 *   · It reads the person's actual record — every mapped field, resolved through the active
 *     perspective's overrides, with the supervisor's NAME rather than their id.
 *   · It edits those fields, which on a scenario writes an override and on as-is changes the underlying
 *     data. That distinction is stated on screen because it is the difference between "in this option"
 *     and "in the company".
 *   · It designates heads, up the person's whole group path plus the organisation itself — the thing the
 *     Data panel's heads-coverage line has been pointing at.
 *
 * The carve-out stage arrived with the Transfers phase: whether somebody joins with the dedicated
 * perimeter or transfers in at Day 1 is a per-person decision, and this is where it is made.
 *
 * The TSA phase added the Day 2 exit alongside it: whether a Day 1 position is dissolved again when the
 * transitional agreements run out.
 *
 * The RemainCo phase added the stranded plan, and the build-up phase the wave, TSA link and needed-by
 * month that decide when a planned position actually exists.
 *
 * The Target phase added the placement control, which appears where the app puts it: on a carve-out
 * person, while you are choosing the perimeter or the transfers.
 */
export function PersonPanel({
  state,
  person,
  perspective,
  phase,
  decisions,
  readOnly,
  onSetBucket,
  onDispatch,
  onOpenAssumptions,
  scenarioId,
}: {
  state: SeparationState;
  person: Person;
  perspective: Perspective;
  /**
   * Which phase this tab is working in, passed rather than read.
   *
   * The placement control below is offered only where the app offers it (`:4593`), and "which phase am
   * I in" is per-user lens state that a panel must never reach for on its own — the same rule the
   * transfers picker follows for its perspective.
   */
  phase: PhaseId;
  decisions: Readonly<Record<string, Bucket>>;
  readOnly: boolean;
  onSetBucket(employeeId: string, bucket: Bucket | null): void;
  onDispatch(command: Command): void;
  onOpenAssumptions(): void;
  scenarioId: string;
}): JSX.Element {
  const bucket = decisions[person.id] ?? 'Remain';
  // The design's own record, not the chart node's copy: the wave, TSA link and needed-by live there.
  const buildUp = person.isBuildUp
    ? state.design.buildUps.find((b) => b.id === person.id) ?? null
    : null;
  const meta = bucketMetaFor(state.design, person.id);
  const joinStage = meta?.joinStage ?? 'perimeter';
  const ruleOwned = meta?.src === 'rule';
  const day2Exit = meta?.day2Exit === true;
  const plan = strandedPlanOf(state.design, person);
  const placement = targetPlacementValues(state, person);
  const strandedCost = (person.dedication ?? 0) * loadedCost(person, state.assumptions);
  const bucketOf = (id: string): string => currentBucket(state.design, id);

  /*
    The person's group path, resolved through the active perspective.

    Ragged hierarchies are the norm — somebody with a Division and no Team — and the chain stops at the
    first blank level rather than skipping it, because the group ids below encode the FULL ancestor path
    and a gap would address a group that does not exist.
  */
  const headRows = useMemo(() => {
    const levels = getActiveLevels(state, perspective);
    const path: GroupPathEntry[] = [];
    for (const level of levels) {
      const value = getEmpField(person, level.key, perspective, state.design.scenarioOverrides);
      if (value == null || String(value).trim() === '') break;
      path.push({ levelKey: level.key, label: level.label, value: String(value) });
    }

    const rows = path.map((entry, i) => {
      const groupId = groupNodeId(path.slice(0, i + 1));
      return {
        groupId,
        label: entry.label,
        value: entry.value,
        isHead: getHeadFor(state, groupId, perspective, bucketOf) === person.id,
        source: getHeadSource(state, groupId, perspective, bucketOf),
      };
    });

    // The whole organisation is headable from anyone's panel, and sits at the top of the list.
    rows.unshift({
      groupId: ORG_HEAD_GROUP,
      label: '',
      value: 'Organisation',
      isHead: getHeadFor(state, ORG_HEAD_GROUP, perspective, bucketOf) === person.id,
      source: getHeadSource(state, ORG_HEAD_GROUP, perspective, bucketOf),
    });
    return rows;
  }, [state, person, perspective]);

  const supervisorId = String(
    getEmpField(person, 'supervisorId', perspective, state.design.scenarioOverrides) ?? '',
  ).trim();
  const supervisor = supervisorId
    ? state.employees.find((e) => String(e.id) === supervisorId) ??
      state.design.buildUps.find((b) => String(b.id) === supervisorId) ??
      null
    : null;
  const showSupervisor = Boolean(state.columnMap.supervisorId || supervisorId);

  const fields = editableFields(state);
  const overrides = state.design.scenarioOverrides[perspective]?.empOverrides?.[person.id] ?? {};

  const toggleHead = (groupId: string, isHead: boolean): void => {
    /*
      Clearing is not always the same command, and which one it is now lives in `removeHeadCommand` —
      because the group panel grew a Remove control and two copies of that rule would drift. The copy
      that used to be here had already drifted: it omitted the bucket check, so it wrote an explicit
      "no head" over groups where nobody had decided anything.
    */
    if (!isHead) {
      onDispatch({ type: 'setHead', scenarioId, perspective, groupId, employeeId: person.id });
      return;
    }
    onDispatch(
      removeHeadCommand(state, { scenarioId, perspective, groupId, headId: String(person.id) }),
    );
  };

  return (
    <>
      <Section
        testId="person-panel"
        title={
          <>
            <span data-testid="selected-name">{person.name}</span>
            {person.isBuildUp && <span className="pill">Build-up</span>}
            {person.openPosition && <span className="pill">○ Open position</span>}
            {person.inactive && <span className="pill">⏸ Inactive</span>}
            {person.isLeaver && <span className="pill">Departed</span>}
            {person.dedication != null ? (
              <span className="pill" data-testid="dedication-pill">
                {person.sharedMarker ? '◐ ' : ''}
                {Math.round(person.dedication * 100)}% dedicated
              </span>
            ) : (
              person.sharedMarker && (
                <span
                  className="pill"
                  title="Legacy shared marker (boolean split-head column) — no numeric dedication"
                >
                  ◐ Shared (legacy marker)
                </span>
              )
            )}
          </>
        }
      >
        <div className="sp-row">
          <span className="k">Personnel ID</span>
          <span className="v">{person.id}</span>
        </div>
        {showSupervisor && (
          <div className="sp-row">
            <span className="k">Reports to</span>
            <span className="v">
              {supervisor ? (
                supervisor.name
              ) : supervisorId ? (
                <>
                  {supervisorId} <span className="muted">(not in data)</span>
                </>
              ) : (
                '—'
              )}
            </span>
          </div>
        )}
        <div className="sp-row">
          <span className="k">Job title</span>
          <span className="v">{person.jobTitle || '—'}</span>
        </div>
        <div className="sp-row">
          <span className="k">Location</span>
          <span className="v">{person.location || '—'}</span>
        </div>
      </Section>

      <Section
        testId="person-bucket"
        title="Bucket assignment"
      >
        <div className="toggle-row">
          {BUCKETS.map((b) => (
            <button
              key={b}
              className={`toggle-btn ${bucket === b ? 'active' : ''}`}
              data-testid={`bucket-${b.replace(/\s+/g, '-')}`}
              aria-pressed={bucket === b}
              // A build-up is a role being created; it cannot also be a role no longer required.
              disabled={readOnly || (person.isBuildUp && b === 'No longer required')}
              onClick={() => onSetBucket(person.id, b)}
            >
              {b}
            </button>
          ))}
        </div>
        {bucket === 'No longer required' &&
          !person.isBuildUp &&
          !person.isLeaver &&
          countsInHeadcount(person) && (
            <SeveranceNote
              state={state}
              person={person}
              lead="Redundant at close — one-off severance estimate:"
              onOpenAssumptions={onOpenAssumptions}
            />
          )}
        {!readOnly && decisions[person.id] && (
          <button data-testid="clear-decision" onClick={() => onSetBucket(person.id, null)}>
            Clear decision
          </button>
        )}
      </Section>

      {/*
        The carve-out stage, ported from `stageSectionHtml` (orgchart.html:5077).

        Only shown for a carve-out member, because stage attribution is what being in the carve-out is
        divided into: the dedicated perimeter chosen in phase 2, or a shared employee transferred in at
        legal separation in phase 3.

        The Day 2 exit checkbox and the build-up wave controls that live in the same section of the
        shipped app belong to the TSA / Day 2 phase, and are not here yet.
      */}
      {bucket === 'Carve-out' && !person.isBuildUp && (
        <Section
          testId="person-stage"
          title="Carve-out stage"
        >
          <div className="sp-row">
            <span className="k">Joins the carve-out</span>
            <span className="v" data-testid="join-label">
              {joinStage === 'day1'
                ? 'Day 1 (transfer)'
                : `Perimeter (${ruleOwned ? 'threshold rule' : 'manual'})`}
            </span>
          </div>
          <div className="toggle-row">
            <button
              className={`toggle-btn ${joinStage !== 'day1' ? 'active' : ''}`}
              data-testid="join-perimeter"
              aria-pressed={joinStage !== 'day1'}
              title="Part of the dedicated workforce selected in phase 2"
              disabled={readOnly}
              onClick={() =>
                onDispatch({
                  type: 'setJoinStage',
                  scenarioId,
                  assignments: [{ employeeId: person.id, joinStage: 'perimeter' }],
                })
              }
            >
              Perimeter
            </button>
            <button
              className={`toggle-btn ${joinStage === 'day1' ? 'active' : ''}`}
              data-testid="join-day1"
              aria-pressed={joinStage === 'day1'}
              title="Shared employee added at legal separation (phase 3)"
              disabled={readOnly}
              onClick={() =>
                onDispatch({
                  type: 'setJoinStage',
                  scenarioId,
                  assignments: [{ employeeId: person.id, joinStage: 'day1' }],
                })
              }
            >
              Day 1 transfer
            </button>
          </div>
          {/*
            The Day 2 exit, ported from `:5085`. In the same section as the stage buttons in the shipped
            app, and it behaves differently from them on purpose: ticking it writes only `day2Exit` and
            leaves the assignment's source alone (`:5113` spreads the existing metadata rather than
            stamping manual, unlike the stage change at `:5100`).

            That asymmetry is right, and the note below it is careful not to over-claim. Saying "this
            person leaves again after Day 1" is a statement about a carve-out member; if the perimeter
            rule later decides they are not a member at all, there is nothing left to exit from. The
            Perimeter panel warns before the slider does that, so the loss is announced rather than
            discovered, and an undo restores it.
          */}
          <label className="check" style={{ marginTop: 8 }}>
            <input
              type="checkbox"
              data-testid="day2-exit"
              checked={day2Exit}
              disabled={readOnly}
              title="Leaves the carve-out organisation again after Day 1"
              onChange={(e) =>
                onDispatch({
                  type: 'setDay2Exit',
                  scenarioId,
                  assignments: [{ employeeId: person.id, day2Exit: e.target.checked }],
                })
              }
            />
            <span>Exits at Day 2 (rightsizing)</span>
          </label>
          {day2Exit && countsInHeadcount(person) && (
            <SeveranceNote
              testId="day2-severance"
              state={state}
              person={person}
              lead="One-off severance estimate:"
              onOpenAssumptions={onOpenAssumptions}
            />
          )}
          {ruleOwned && (
            <p className="muted" data-testid="rule-owned-note">
              Selected by the threshold rule — changing the stage makes the assignment manual (the slider
              will no longer move this person). Marking a Day 2 exit does not: the rule can still move
              them, and the Perimeter panel says so before it does.
            </p>
          )}
        </Section>
      )}

      {/*
        Stranded capacity, ported from `strandedSectionHtml` (orgchart.html:5161).

        The Remain-side counterpart of the carve-out stage section, and shown under exactly the same
        eligibility the engine uses: a real, active, staying person with some dedication to the business
        that left. Somebody with none of their time on the carve-out strands nothing, and rendering an
        empty plan for them would invite a decision there is nothing to decide.
      */}
      {strandedEligible(state.design, person) && (
        <Section
          testId="person-stranded"
          title={
            <>
              Stranded capacity (RemainCo){' '}
              <span className="pill" data-testid="stranded-badge">
                {plan
                  ? `${plan.action === 'rightsize' ? '✕' : '↻'} M${plan.month}`
                  : fmtMoneyCompact(strandedCost)}
              </span>
            </>
          }
        >
          <p className="muted">
            {Math.round((person.dedication ?? 0) * 100)}% of this person&rsquo;s work serves the carve-out
            — <strong>{fmtMoney(strandedCost)}</strong> loaded p.a. is stranded in RemainCo after Day 1.
          </p>
          {/*
            An open position or an inactive person can be eligible for a plan while every figure that
            reads one excludes them. The control stays — an imported file can carry such an entry and it
            has to be removable — but it says what it is worth.
          */}
          {!countsInHeadcount(person) && (
            <p className="muted" data-testid="stranded-not-counted">
              {person.openPosition ? 'An open position' : 'An inactive person'} carries no filled
              headcount, so a plan here changes no figure on the RemainCo panel.
            </p>
          )}
          <div className="toggle-row">
            {STRANDED_ACTIONS.map((a) => (
              <button
                key={a.key}
                className={`toggle-btn ${(plan ? plan.action : 'keep') === a.key ? 'active' : ''}`}
                data-testid={`stranded-${a.key}`}
                aria-pressed={(plan ? plan.action : 'keep') === a.key}
                title={a.title}
                disabled={readOnly}
                onClick={() =>
                  onDispatch({
                    type: 'setStrandedPlan',
                    scenarioId,
                    assignments: [
                      {
                        employeeId: person.id,
                        action: a.key === 'keep' ? null : a.key,
                        /*
                          Six months on a new plan, matching the app (`:5167`, `:6441`) — how long it
                          takes to act on a stranded role. The command schema defaults this field to 0,
                          which says something else entirely: effective immediately. Sent explicitly so
                          the platform's answer is the app's.
                        */
                        month: plan ? plan.month : 6,
                      },
                    ],
                  })
                }
              >
                {a.label}
              </button>
            ))}
          </div>
          {plan && (
            <>
              <div className="field" style={{ marginTop: 8 }}>
                <label htmlFor="stranded-month">Effective month (after Day 1)</label>
                <LiveField
                  id="stranded-month"
                  data-testid="stranded-month"
                  type="number"
                  /*
                    M1 is the floor, not M0.

                    The model collapses month 0 into month 1 — the carrier stops counting at
                    `max(M, 1)` and severance books in the same month — while the history recorded
                    "M0". Nothing is lost by the collapse; what was wrong is that the audit trail said
                    one month and the chart drew another. Offering only what the model can distinguish
                    is the same rule as not offering a status the charts cannot honour.
                  */
                  min={1}
                  max={36}
                  step={1}
                  value={String(plan.month)}
                  disabled={readOnly}
                  onCommit={(text, el) => {
                    // Clamped, then written straight back to the field, so it never states a month the
                    // model does not hold (`:5198`). That write is what makes the remount unnecessary.
                    const month = Math.max(1, Math.min(36, Math.round(Number(text) || 0)));
                    el.value = String(month);
                    if (month === plan.month) return;
                    onDispatch({
                      type: 'setStrandedPlan',
                      scenarioId,
                      assignments: [{ employeeId: person.id, action: plan.action, month }],
                    });
                  }}
                />
              </div>
              {plan.action === 'rightsize' ? (
                <p className="muted">
                  One-off severance ≈{' '}
                  <strong>{fmtMoney(severanceCostOf(person, state.assumptions))}</strong> (
                  {fmt(severanceMonths(person, state.assumptions), 1)} months of base salary,{' '}
                  <AssumptionsLink testId="asm-open-person" onOpen={onOpenAssumptions}>
                    {tierNote(person, state)}
                  </AssumptionsLink>
                  ); full loaded cost {fmtMoney(loadedCost(person, state.assumptions))} p.a. saved from M
                  {plan.month}.
                  {(person.dedication ?? 0) < 1 &&
                    ` Also removes the ${Math.round((1 - (person.dedication ?? 0)) * 100)}% of their capacity RemainCo still uses (over-removal / backfill risk).`}
                </p>
              ) : (
                <p className="muted">
                  Capacity repurposed from M{plan.month} — no cash saving, no severance; a deliberate
                  accept decision.
                </p>
              )}
            </>
          )}
        </Section>
      )}

      {/*
        Where this person sits in the organisation being designed.

        Gated as the app gates it: the design must be on, the person must be going to the carve-out —
        the blueprint describes the new organisation, so somebody staying behind has no box in it — and
        it appears while you are choosing the perimeter or the transfers, which is when the question
        comes up. `targetUnitOptions` expands every declared path into its prefixes, so a person can be
        placed at any tier rather than only in a leaf.
      */}
      {state.targetEnabled && bucket === 'Carve-out' && (phase === 'perimeter' || phase === 'transfers') && (
        <Section
          testId="person-target"
          title={
            <>
              Target org{' '}
              <span className="pill" data-testid="person-target-badge">
                {placement.length ? placement.join(' › ') : 'unplaced'}
              </span>
            </>
          }
        >
          <div className="field">
            <label htmlFor="person-target-place">Target unit</label>
            <select
              id="person-target-place"
              data-testid="person-target-place"
              // Matched by VALUE rather than by the ' › ' label: two different paths can render the
              // same label if a level value contains the separator, and the value is the identity.
              value={placement.length ? JSON.stringify(placement) : ''}
              disabled={readOnly}
              onChange={(e) =>
                onDispatch({
                  type: 'setTargetPlacement',
                  scenarioId,
                  assignments: [
                    {
                      employeeId: person.id,
                      path: e.target.value ? (JSON.parse(e.target.value) as string[]) : null,
                    },
                  ],
                })
              }
            >
              <option value="">(unplaced)</option>
              {targetUnitOptions(state).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {!placement.length && (
            <p className="muted">
              Unplaced people are not drawn in the target view at all — the blueprint shows the
              organisation as designed, so somebody with no unit has nowhere to appear.
            </p>
          )}
        </Section>
      )}

      {buildUp && (
        <BuildUpStage
          state={state}
          buildUp={buildUp}
          bucket={bucket}
          readOnly={readOnly}
          scenarioId={scenarioId}
          onDispatch={onDispatch}
          onOpenAssumptions={onOpenAssumptions}
        />
      )}

      {buildUp ? (
        <BuildUpFields
          state={state}
          buildUp={buildUp}
          readOnly={readOnly}
          scenarioId={scenarioId}
          onDispatch={onDispatch}
        />
      ) : (
      <Section
        testId="person-fields"
        title={<>Edit fields · {PERSPECTIVE_LABEL[perspective]}</>}
      >
        <p className="muted">
          {perspective === 'as-is'
            ? 'Edits change the underlying data.'
            : `Edits apply to the ${PERSPECTIVE_LABEL[perspective]} view only; as-is is unchanged.`}
        </p>
        {fields.map((field) => (
          <FieldEditor
            key={field.key}
            state={state}
            person={person}
            perspective={perspective}
            field={field}
            overridden={overrides[field.key] !== undefined}
            readOnly={readOnly}
            onCommit={(value) =>
              onDispatch({
                type: 'setEmpField',
                scenarioId,
                perspective,
                employeeId: person.id,
                fieldKey: field.key,
                value,
              })
            }
            onRevert={() =>
              onDispatch({
                type: 'setEmpField',
                scenarioId,
                perspective,
                employeeId: person.id,
                fieldKey: field.key,
                value: null,
              })
            }
          />
        ))}
      </Section>
      )}

      <Section
        testId="person-heads"
        title={<>Head designation · {PERSPECTIVE_LABEL[perspective]}</>}
      >
        {state.groupingLevels.length === 0 ? (
          <p className="muted">No grouping levels configured.</p>
        ) : (
          headRows.map((row) => (
            <button
              key={row.groupId}
              className={`toggle-btn wide ${row.isHead ? 'active' : ''}`}
              data-testid={`head-${row.groupId}`}
              aria-pressed={row.isHead}
              disabled={readOnly}
              onClick={() => toggleHead(row.groupId, row.isHead)}
            >
              {row.isHead ? '★ ' : ''}Head of {row.value}{' '}
              <span className="dim">({row.label || 'whole org'})</span>
              {row.isHead && row.source === 'inherited' && <span className="pill">inherited</span>}
            </button>
          ))
        )}
        <p className="muted">
          Heads are scoped to the view you are in.
          {perspective === 'remain' && ' RemainCo inherits the as-is head while that person still stays.'}{' '}
          As-is and the other perspectives are unaffected.
        </p>
      </Section>

      {/*
        Deleting a planned hire, ported from `spDeleteBu` (`:5471`).

        Only build-ups: a real employee is never deleted, because they still price severance and anchor
        the audit trail — leaving the organisation goes through the leaver flow. A hire that was never
        made has neither, so removing the position is the honest operation, and it is the one thing
        "No longer required" is NOT (the handler refuses that, and says so).

        No confirmation, because it is undoable: the inverse restores the row, its bucket and every head
        designation it held. The app's is not undoable and asks nothing either.
      */}
      {buildUp && !readOnly && (
        <section data-testid="buildup-delete">
          <button
            className="danger"
            data-testid="buildup-delete-button"
            onClick={() =>
              onDispatch({ type: 'deleteBuildUp', scenarioId, buildUpId: buildUp.id })
            }
          >
            Delete build-up
          </button>
          <p className="muted">
            Removes the position, its assignment and any group it heads. Undoable from the history.
          </p>
        </section>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

/**
 * One field, edited as a draft.
 *
 * Committed on blur or Enter rather than on every keystroke: each commit is a command and an event in
 * the change log, and typing a location one letter at a time should not write eight of them.
 */
function FieldEditor({
  state,
  person,
  perspective,
  field,
  overridden,
  readOnly,
  onCommit,
  onRevert,
}: {
  state: SeparationState;
  person: Person;
  perspective: Perspective;
  field: { key: string; label: string; numeric: boolean };
  overridden: boolean;
  readOnly: boolean;
  onCommit(value: string | number | null): void;
  onRevert(): void;
}): JSX.Element {
  // resolveField rather than the stored value, so openPosition and inactive read "Open" / "Inactive"
  // instead of a boolean nobody typed.
  const current = resolveField(state, person, field.key, perspective);
  const shown = current == null ? '' : String(current);
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? shown;

  const commit = (): void => {
    if (draft === null || draft === shown) {
      setDraft(null);
      return;
    }
    if (field.numeric) {
      const trimmed = draft.trim();
      // A cleared numeric field is "no value", not zero — a salary of 0 and an unknown salary price
      // very differently.
      onCommit(trimmed === '' ? null : Number(trimmed.replace('%', '').replace(',', '.')));
    } else {
      onCommit(draft.trim());
    }
    setDraft(null);
  };

  return (
    <div className="field">
      <label htmlFor={`edit-${field.key}`}>
        {field.label}
        {overridden && <span className="pill">overridden</span>}
      </label>
      <div className="field-row">
        <input
          id={`edit-${field.key}`}
          data-testid={`edit-${field.key}`}
          type={field.numeric ? 'number' : 'text'}
          step={field.key === 'fte' || field.key === 'dedication' ? '0.05' : '1'}
          {...(field.key === 'dedication' ? { min: 0, max: 1, placeholder: '0–1 or %' } : {})}
          value={value}
          disabled={readOnly}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setDraft(null);
          }}
        />
        {overridden && !readOnly && (
          <button title="Revert to the as-is value" data-testid={`revert-${field.key}`} onClick={onRevert}>
            ↺
          </button>
        )}
      </div>
      {overridden && (
        <div className="muted">
          As-is: {String(getEmpField(person, field.key, 'as-is', undefined) ?? '') || '—'}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * A planned hire's timing
 * ------------------------------------------------------------------ */

/**
 * The build-up branch of `stageSectionHtml` (orgchart.html:5057) plus `remainTsaSectionHtml` (`:5042`).
 *
 * A planned hire has a stage like anybody else, but a different one. An employee's stage says how they
 * arrive in the carve-out — with the dedicated perimeter, or as a Day 1 transfer. A hire's says WHEN the
 * position exists: Day 1 means it stands up at legal separation, Day 2 means it arrives afterwards, and
 * a Day 2 hire is usually there to let a transitional agreement end, which is what the TSA link records.
 *
 * Both sides of the deal build replacements, which is why this renders for a Remain hire too. When
 * RemainCo provides a service under a reverse TSA, the carve-out is the one being served; when the
 * agreement ends it is REMAINCO that has to run the service itself, so the hire that internalises it
 * sits on the Remain side. The section only appears there if such an agreement exists or the hire is
 * already linked to one — otherwise it is a control with nothing to point at.
 *
 * The exclusivity of the three fields is enforced in the handler, not here: Day 1 clears the TSA link
 * and the needed-by month, Day 2 clears the Day 2 exit. The UI shows what applies; the model guarantees
 * it.
 */
function BuildUpStage({
  state,
  buildUp,
  bucket,
  readOnly,
  scenarioId,
  onDispatch,
  onOpenAssumptions,
}: {
  state: SeparationState;
  buildUp: BuildUp;
  bucket: Bucket;
  readOnly: boolean;
  scenarioId: string;
  onDispatch(command: Command): void;
  onOpenAssumptions(): void;
}): JSX.Element | null {
  const side: Bucket = bucket === 'Carve-out' ? 'Carve-out' : 'Remain';
  const linked = buildUp.tsaId ? state.design.tsas.find((t) => t.id === buildUp.tsaId) ?? null : null;
  const wrongSide = linked !== null && tsaReplacementSide(linked) !== side;

  /*
    The agreements THIS side would have to replace — plus, always, the one already linked.

    A carve-out hire covers a received service; a Remain hire internalises a provided one, so the offer
    is side-filtered. But a link can go wrong-side without anybody touching the hire: flipping a TSA's
    direction in the register is one click, and `updateTsa` deliberately rewrites only the TSA row. If
    the current link were then filtered out, the select would have a value matching no option and render
    blank — the panel denying a link the row still holds, and denying exactly the state diagnostics
    sends the user here to fix. The original keeps it visible with a ⚠ (`tsaOptsFor`, `:5034`).
  */
  const options = state.design.tsas.filter(
    (t) => tsaReplacementSide(t) === side || t.id === buildUp.tsaId,
  );

  // A Remain-side hire with nothing to internalise has no timing decision to make.
  if (side === 'Remain' && !options.length) return null;

  const carve = side === 'Carve-out';
  const wave = buildUp.wave;
  const update = (patch: Record<string, unknown>): void => {
    onDispatch({ type: 'updateBuildUp', scenarioId, buildUpId: buildUp.id, patch });
  };

  return (
    <Section
      testId="buildup-stage"
      title={
        <>
          {carve ? 'Carve-out stage' : 'TSA replacement (RemainCo)'}{' '}
          <span className="pill" data-testid="buildup-wave-badge">
            {carve ? (wave === 'day2' ? 'Day 2' : 'Day 1') : linked ? `⇠ ${linked.name}` : 'Unlinked'}
          </span>
        </>
      }
    >

      {carve ? (
        <div className="toggle-row">
          <button
            className={`toggle-btn ${wave === 'day1' ? 'active' : ''}`}
            data-testid="buildup-wave-day1"
            aria-pressed={wave === 'day1'}
            title="Position exists from legal separation"
            disabled={readOnly}
            onClick={() => wave !== 'day1' && update({ wave: 'day1' })}
          >
            Day 1
          </button>
          <button
            className={`toggle-btn ${wave === 'day2' ? 'active' : ''}`}
            data-testid="buildup-wave-day2"
            aria-pressed={wave === 'day2'}
            title="Post-separation build-up (e.g. TSA replacement)"
            disabled={readOnly}
            onClick={() => wave !== 'day2' && update({ wave: 'day2' })}
          >
            Day 2
          </button>
        </div>
      ) : (
        <p className="muted">
          When a provided (reverse) TSA ends, RemainCo must run the service itself — link this hire to
          the agreement it internalises.
        </p>
      )}

      {(!carve || wave === 'day2') && (
        <>
          <div className="field" style={{ marginTop: 8 }}>
            <label htmlFor="buildup-tsa">Replaces TSA</label>
            <select
              id="buildup-tsa"
              data-testid="buildup-tsa"
              value={buildUp.tsaId ?? ''}
              disabled={readOnly}
              onChange={(e) => update({ tsaId: e.target.value || null })}
            >
              <option value="">— No TSA link —</option>
              {options.map((t) => (
                <option key={t.id} value={t.id}>
                  {tsaReplacementSide(t) === side ? '' : '⚠ '}
                  {t.direction === 'provided' ? '⇠' : '⇢'} {t.name}
                  {t.durationMonths ? ` (exit M${t.durationMonths})` : ''}
                </option>
              ))}
            </select>
          </div>
          {wrongSide && (
            <p className="muted" data-testid="buildup-wrong-side">
              &ldquo;{linked.name}&rdquo; is replaced by the {tsaReplacementSide(linked)} side, and this
              hire sits in {side} — so nothing counts it as the replacement. Move the hire to{' '}
              {tsaReplacementSide(linked)} or clear the link.
            </p>
          )}
          <div className="field">
            <label htmlFor="buildup-needed-by">Needed by (months after Day 1)</label>
            <LiveField
              id="buildup-needed-by"
              data-testid="buildup-needed-by"
              type="number"
              min={0}
              /*
                On the TSA clock (0..120), not the stranded plan's 0..36.

                While every chart axis clamped to 36 the difference was invisible. It stopped being so
                when the axis started following the data: an agreement can run to M120, so capping its
                replacement at M36 meant a replacement could not be dated after the service it replaces
                ends — `late` could never fire and the exit read as covered by construction, for exactly
                the long agreements where the question matters most.
              */
              max={120}
              step={1}
              placeholder="e.g. 9"
              // The clamp is written back by the commit below, which is what puts a bounded month in the
              // field. The key that used to sit here added nothing to that and destroyed the box under
              // anyone typing when a colleague dated the same replacement.
              value={buildUp.neededBy == null ? '' : String(buildUp.neededBy)}
              disabled={readOnly}
              onCommit={(text, el) => {
                const raw = text.trim();
                const next = raw === '' ? null : Math.max(0, Math.min(120, Math.round(Number(raw) || 0)));
                el.value = next == null ? '' : String(next);
                if (next === (buildUp.neededBy ?? null)) return;
                update({ neededBy: next });
              }}
            />
          </div>
          {linked && linked.durationMonths != null && buildUp.neededBy != null &&
            buildUp.neededBy > linked.durationMonths && (
              <p className="muted" data-testid="buildup-late">
                Needed by M{buildUp.neededBy} but &ldquo;{linked.name}&rdquo; ends at M
                {linked.durationMonths} — the service would run uncovered for{' '}
                {buildUp.neededBy - linked.durationMonths} month
                {buildUp.neededBy - linked.durationMonths === 1 ? '' : 's'}.
              </p>
            )}
        </>
      )}

      {carve && wave === 'day1' && (
        <label className="check" style={{ marginTop: 8 }} title="This Day 1 position is dissolved again at Day 2">
          <input
            type="checkbox"
            data-testid="buildup-day2-exit"
            checked={buildUp.day2Exit}
            disabled={readOnly}
            onChange={(e) => update({ day2Exit: e.target.checked })}
          />{' '}
          Exits at Day 2 (rightsizing)
        </label>
      )}

      {countsInHeadcount(buildUp) && (
        <p className="muted" data-testid="buildup-hiring">
          One-off hiring cost estimate:{' '}
          <strong>{fmtMoney(hiringCostOf(buildUp, state.assumptions))}</strong> (
          {state.assumptions.hiringCostPct}% of annual salary —{' '}
          <AssumptionsLink testId="asm-open-person-hiring" onOpen={onOpenAssumptions}>
            cost assumptions
          </AssumptionsLink>
          ).
        </p>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------ *
 * A planned hire's own record
 * ------------------------------------------------------------------ */

/** The fields a build-up row owns, in the order the creation form asks for them. */
const BUILDUP_FIELDS: readonly { key: string; label: string; numeric?: boolean }[] = [
  { key: 'name', label: 'Name' },
  { key: 'jobTitle', label: 'Job title' },
  { key: 'salary', label: 'Salary', numeric: true },
  { key: 'fte', label: 'FTE', numeric: true },
  { key: 'location', label: 'Location' },
  { key: 'seniority', label: 'Seniority' },
  { key: 'country', label: 'Country' },
  { key: 'legalEntity', label: 'Legal entity' },
];

/**
 * Editing a position, rather than overriding a record that does not exist.
 *
 * `setEmpField` is the wrong instrument for a planned hire and now says so. On as-is it always
 * refused — there is no imported record. On a scenario it used to SUCCEED, writing an override keyed by
 * the hire's id which `getEmpField` faithfully returned to this panel, while `hiringCostOf`, both stage
 * bridges and the exporter kept reading the build-up row. A user corrected a salary, watched the field
 * change, and every number derived from it stayed on the old value.
 *
 * So a hire's fields are edited through `updateBuildUp`, which writes the row every one of those
 * surfaces reads. There is no per-perspective variant, and that is faithful: a build-up belongs to one
 * scenario already, so "in this option only" is what it always is.
 */
function BuildUpFields({
  state,
  buildUp,
  readOnly,
  scenarioId,
  onDispatch,
}: {
  state: SeparationState;
  buildUp: BuildUp;
  readOnly: boolean;
  scenarioId: string;
  onDispatch(command: Command): void;
}): JSX.Element {
  const rows: readonly { key: string; label: string; numeric?: boolean }[] = [
    ...BUILDUP_FIELDS,
    ...state.groupingLevels.map((l) => ({ key: `levels.${l.key}`, label: l.label })),
  ];

  const valueOf = (key: string): string => {
    if (key.startsWith('levels.')) return String(buildUp.levels[key.slice(7)] ?? '');
    return String((buildUp as unknown as Record<string, unknown>)[key] ?? '');
  };

  const commit = (key: string, raw: string, numeric: boolean): void => {
    const text = raw.trim();
    if (text === valueOf(key)) return;
    if (key.startsWith('levels.')) {
      // The whole map, because a patch replaces the field rather than merging into it.
      const levelKey = key.slice(7);
      onDispatch({
        type: 'updateBuildUp',
        scenarioId,
        buildUpId: buildUp.id,
        patch: { levels: { ...buildUp.levels, [levelKey]: text || null } },
      });
      return;
    }
    // A name is the one field that cannot be blank — the creation form requires it, and a nameless
    // card on the chart is not something the user can then click on to fix.
    if (key === 'name' && !text) return;
    const value = numeric ? (text === '' ? null : Number(text) || 0) : text || null;
    onDispatch({
      type: 'updateBuildUp',
      scenarioId,
      buildUpId: buildUp.id,
      patch: { [key]: value },
    });
  };

  return (
    <Section
      testId="buildup-fields"
      title="Edit position"
    >
      <p className="muted">
        A planned hire has no imported record, so these edit the position itself — the same values
        every cost and headcount figure reads. The position belongs to this scenario alone.
      </p>
      {rows.map((f) => (
        <div className="field" key={f.key}>
          <label htmlFor={`bu-edit-${f.key}`}>{f.label}</label>
          <LiveField
            id={`bu-edit-${f.key}`}
            data-testid={`bu-edit-${f.key}`}
            type={f.numeric ? 'number' : 'text'}
            step={f.key === 'fte' ? '0.05' : '1'}
            // Resynced when the stored value moves and never while somebody is in the box. A refusal
            // leaves the value alone, so the key this replaces never delivered the restore it claimed.
            value={valueOf(f.key)}
            disabled={readOnly}
            onCommit={(text) => commit(f.key, text, f.numeric === true)}
          />
        </div>
      ))}
    </Section>
  );
}

/* ------------------------------------------------------------------ *
 * Cost assumptions, reachable from where they are read
 * ------------------------------------------------------------------ */

/**
 * A link into the cost assumptions, styled as text rather than a button.
 *
 * Four panels printed "see Assumptions" as plain prose pointing at a surface that did not exist on
 * the platform. Prose that names a control is a promise; this is the control.
 */
function AssumptionsLink({
  testId,
  onOpen,
  children,
}: {
  testId: string;
  onOpen(): void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button type="button" className="linkish" data-testid={testId} onClick={onOpen}>
      {children}
    </button>
  );
}

/** Which rule set this person's severance months, in the words the editor uses. */
function tierNote(person: Person, state: SeparationState): string {
  switch (severanceTier(person, state.assumptions)) {
    case 'seniority':
      return `${String(person.seniority)} grade rule`;
    case 'country':
      return `${String(person.country)} country rule`;
    default:
      return 'default rule';
  }
}

/**
 * One person's severance estimate, and the rule that produced it.
 *
 * Naming the winning tier is the addition: `severanceMonths` resolves seniority → country → default
 * (`cost.ts:36-45`) and the panel used to print only the answer, so two people on the same salary
 * showing different figures had no explanation anywhere on screen. The tier comes from core rather
 * than being re-derived here, because a panel that disagreed with the engine about the order of the
 * chain would be worse than no explanation at all.
 */
function SeveranceNote({
  testId,
  state,
  person,
  lead,
  onOpenAssumptions,
}: {
  testId?: string;
  state: SeparationState;
  person: Person;
  lead: string;
  onOpenAssumptions(): void;
}): JSX.Element {
  return (
    <p className="muted" data-testid={testId}>
      {lead} <strong>{fmtMoney(severanceCostOf(person, state.assumptions))}</strong> (
      {fmt(severanceMonths(person, state.assumptions), 1)} months of base salary,{' '}
      <AssumptionsLink testId="asm-open-person" onOpen={onOpenAssumptions}>
        {tierNote(person, state)}
      </AssumptionsLink>
      ).
    </p>
  );
}
